// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Material estimate.
 *
 * The usual "volume × density × infill%" shortcut is wrong for most printed
 * parts, and badly wrong for small ones: perimeters and the top/bottom skins
 * are solid regardless of infill, and on a small part they *are* the part. So
 * the walls are modelled explicitly — surface area × wall thickness gives the
 * solid shell, and only what is left over is filled at the infill ratio.
 *
 * It is still an estimate. A slicer knows the layer height, the perimeter
 * count, the infill pattern and the actual travel; this knows the geometry. It
 * is presented as a ballpark and labelled as one.
 */

export interface Material {
  id: string;
  name: string;
  /** g/cm³ */
  density: number;
  /** Typical retail price in USD per kilogram. */
  pricePerKg: number;
  kind: 'filament' | 'resin';
}

export const MATERIALS: Material[] = [
  { id: 'pla', name: 'PLA', density: 1.24, pricePerKg: 22, kind: 'filament' },
  { id: 'petg', name: 'PETG', density: 1.27, pricePerKg: 24, kind: 'filament' },
  { id: 'abs', name: 'ABS', density: 1.04, pricePerKg: 23, kind: 'filament' },
  { id: 'asa', name: 'ASA', density: 1.07, pricePerKg: 30, kind: 'filament' },
  { id: 'tpu', name: 'TPU (flexible)', density: 1.21, pricePerKg: 34, kind: 'filament' },
  { id: 'nylon', name: 'Nylon (PA)', density: 1.14, pricePerKg: 45, kind: 'filament' },
  { id: 'pc', name: 'Polycarbonate', density: 1.2, pricePerKg: 45, kind: 'filament' },
  { id: 'plaw', name: 'Wood-fill PLA', density: 1.28, pricePerKg: 32, kind: 'filament' },
  { id: 'resin', name: 'Standard resin', density: 1.1, pricePerKg: 35, kind: 'resin' },
];

export interface Estimate {
  /** Enclosed volume of the solid, cm³. */
  solidCm3: number;
  /** Volume of material actually extruded, cm³. */
  materialCm3: number;
  grams: number;
  /** Filament length in metres, or null for resin. */
  metres: number | null;
  costUsd: number;
}

/** Three 0.4mm perimeters — the near-universal default profile. */
export const DEFAULT_WALL_MM = 1.2;

const FILAMENT_DIAMETER_MM = 1.75;

export function estimateMaterial(
  volumeMm3: number,
  areaMm2: number,
  infill: number,
  material: Material,
  wallMm: number = DEFAULT_WALL_MM,
): Estimate {
  const clampedInfill = Math.min(Math.max(infill, 0), 1);
  const shellMm3 = Math.min(areaMm2 * wallMm, volumeMm3);
  const interiorMm3 = Math.max(volumeMm3 - shellMm3, 0);
  // Resin prints solid — there is no infill to skip.
  const usedMm3 =
    material.kind === 'resin' ? volumeMm3 : shellMm3 + interiorMm3 * clampedInfill;

  const materialCm3 = usedMm3 / 1000;
  const grams = materialCm3 * material.density;
  const radius = FILAMENT_DIAMETER_MM / 2;
  const metres =
    material.kind === 'filament' ? usedMm3 / (Math.PI * radius * radius) / 1000 : null;

  return {
    solidCm3: volumeMm3 / 1000,
    materialCm3,
    grams,
    metres,
    costUsd: (grams / 1000) * material.pricePerKg,
  };
}

export function findMaterial(id: string): Material {
  return MATERIALS.find((m) => m.id === id) ?? MATERIALS[0];
}
